import { useCallback, useEffect, useRef, useState } from 'react';
import { auth, errorMessage } from '../api/client';
import { useStore } from '../state/store';
import { href } from '../router';
import { Icon } from '../components/ui';
import { FieldError, PASSWORD_MIN_LENGTH, StorySide, fieldErrors } from './AuthPages';
import '../styles/pages/discover.css';

/**
 * Nothing on these pages says whether an address has an account or why a link failed: the copy is
 * the same for an unknown address, a spent link and an expired one, matching what the API answers.
 */
const NEUTRAL_SENT = 'If that address has an account, a reset link is on its way. It works once and expires in 60 minutes.';
const NEUTRAL_BAD_LINK = 'That link is no longer valid. It may have been used already or expired.';

const RESET_STORY = {
  heading: 'Your work at TechCore is waiting.',
  points: [
    { icon: 'lock' as const, title: 'Only you can reset it', body: 'The link goes to your inbox, works once, and expires in an hour.' },
    { icon: 'briefcase' as const, title: 'Nothing is lost', body: 'Sessions, deliverables and skill evidence stay exactly as you left them.' },
    { icon: 'shield' as const, title: 'Everything else signs out', body: 'Choosing a new password ends every other signed-in device.' },
  ],
  note: 'We never ask for your password by email. Links in a WorkSim AI message always open this site.',
};

/** Step one of a reset: ask for the link. The answer is the same whatever address is entered. */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string>();
  const [fieldError, setFieldError] = useState<string>();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setFieldError(undefined);
    if (!email.trim()) {
      setFieldError('Enter your email address.');
      return;
    }
    setBusy(true);
    try {
      await auth.forgot(email.trim());
      setSent(true);
    } catch (err) {
      const f = fieldErrors(err);
      if (f.email) setFieldError(f.email);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth page-enter">
      <div className="form-side">
        <article className="form-inner">
          <span className="eyebrow">Account recovery</span>
          <h1>Forgot your password?</h1>
          {sent ? (
            <>
              <p className="muted">Enter your email address and we will send a link to choose a new password.</p>
              <div className="notice ok" role="status" aria-live="polite">
                <strong style={{ display: 'block' }}>Check your inbox</strong>
                {NEUTRAL_SENT}
              </div>
              <p className="small muted">No message after a few minutes? Check the spam folder, then <button type="button" className="link-button" onClick={() => setSent(false)}>try another address</button>.</p>
              <div className="actions">
                <a className="button" href={href({ page: 'login' })}>Back to log in</a>
              </div>
            </>
          ) : (
            <>
              <p className="muted">Enter the address you signed up with. We will send a link to choose a new password.</p>
              <form onSubmit={(e) => void submit(e)} noValidate aria-busy={busy}>
                <div className="field">
                  <label htmlFor="forgot-email">Email</label>
                  <input id="forgot-email" type="text" inputMode="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={fieldError ? true : undefined} aria-describedby={fieldError ? 'forgot-email-err' : undefined} />
                  <FieldError id="forgot-email-err" message={fieldError} />
                </div>
                <div aria-live="polite">{error && <div className="notice bad" role="alert">{error}</div>}</div>
                <div className="actions">
                  <button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send reset link'}</button>
                  <a className="button quiet" href={href({ page: 'login' })}>Back to log in</a>
                </div>
              </form>
            </>
          )}
        </article>
      </div>
      <StorySide {...RESET_STORY} />
    </div>
  );
}

/** Step two: the link from the email, carrying a single-use token in the hash query. */
export function ResetPasswordPage({ token }: { token?: string }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string>();
  const [fields, setFields] = useState<Record<string, string>>({});

  const strength = password.length === 0 ? 0 : password.length < PASSWORD_MIN_LENGTH ? 1 : password.length < 16 ? 2 : 3;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    const local: Record<string, string> = {};
    if (password.length < PASSWORD_MIN_LENGTH) local.password = `Use at least ${PASSWORD_MIN_LENGTH} characters.`;
    if (confirm !== password) local.confirm = 'The two passwords do not match.';
    setFields(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      await auth.reset(token ?? '', password);
      setDone(true);
    } catch (err) {
      // A complaint about the token belongs with the link, not with a field on this form.
      const f = fieldErrors(err);
      const mapped: Record<string, string> = {};
      if (f.newPassword) mapped.password = f.newPassword;
      if (f.confirm) mapped.confirm = f.confirm;
      setFields(mapped);
      if (Object.keys(mapped).length === 0) setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const body = () => {
    if (done) {
      return (
        <>
          <div className="notice ok" role="status" aria-live="polite">
            <strong style={{ display: 'block' }}>Your password has been changed</strong>
            Every other signed-in device has been signed out, and your email address now counts as verified.
          </div>
          <div className="actions">
            <a className="button" href={href({ page: 'login' })}>Log in</a>
          </div>
        </>
      );
    }
    if (!token) {
      return (
        <>
          <div className="notice bad" role="alert">{NEUTRAL_BAD_LINK}</div>
          <div className="actions">
            <a className="button" href={href({ page: 'forgot' })}>Request another link</a>
            <a className="button quiet" href={href({ page: 'login' })}>Back to log in</a>
          </div>
        </>
      );
    }
    return (
      <form onSubmit={(e) => void submit(e)} noValidate aria-busy={busy}>
        <div className="field">
          <label htmlFor="reset-password">New password</label>
          <input id="reset-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={fields.password ? true : undefined} aria-describedby="reset-password-hint reset-password-err" />
          <div className="strength" aria-hidden="true">
            <i className={strength >= 1 ? 'on' : ''} />
            <i className={strength >= 2 ? 'on' : ''} />
            <i className={strength >= 3 ? 'on full' : ''} />
          </div>
          <div id="reset-password-hint" className="hint">At least {PASSWORD_MIN_LENGTH} characters. A short sentence is easier to remember than symbols. It must not repeat one character or contain your email name.</div>
          <FieldError id="reset-password-err" message={fields.password} />
        </div>
        <div className="field">
          <label htmlFor="reset-confirm">Confirm new password</label>
          <input id="reset-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} aria-invalid={fields.confirm ? true : undefined} aria-describedby={fields.confirm ? 'reset-confirm-err' : undefined} />
          <FieldError id="reset-confirm-err" message={fields.confirm} />
        </div>
        <div aria-live="polite">
          {error && (
            <div className="notice bad" role="alert">
              {error}
              <span style={{ display: 'block', marginTop: '0.4rem' }}><a href={href({ page: 'forgot' })}>Request another link</a></span>
            </div>
          )}
        </div>
        <div className="actions">
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Set new password'}</button>
          <a className="button quiet" href={href({ page: 'login' })}>Cancel</a>
        </div>
      </form>
    );
  };

  return (
    <div className="auth page-enter">
      <div className="form-side">
        <article className="form-inner">
          <span className="eyebrow">Account recovery</span>
          <h1>{done ? 'Password changed' : 'Choose a new password'}</h1>
          {!done && token && <p className="muted">Pick something you have not used elsewhere. Setting it signs out every other device.</p>}
          {body()}
        </article>
      </div>
      <StorySide {...RESET_STORY} />
    </div>
  );
}

/** The link from a verification email. The token is spent as soon as the page opens. */
export function VerifyEmailPage({ token }: { token?: string }) {
  const { user, setUser } = useStore();
  const [state, setState] = useState<'working' | 'ok' | 'failed'>(token ? 'working' : 'failed');
  const [resent, setResent] = useState<'idle' | 'busy' | 'sent' | 'failed'>('idle');
  // The token is single use, so a second run of the effect (a remount, or development's double
  // invocation) must wait on the first call rather than spend the link again.
  const attempt = useRef<{ token: string; promise: Promise<unknown> } | undefined>(undefined);

  useEffect(() => {
    if (!token) {
      setState('failed');
      return;
    }
    let canceled = false;
    setState('working');
    if (attempt.current?.token !== token) attempt.current = { token, promise: auth.verify(token) };
    void attempt.current.promise.then(
      async () => {
        if (canceled) return;
        setState('ok');
        // Read the user back rather than patching it, so a signed-in tab cannot keep a stale flag.
        try {
          const { user: fresh } = await auth.me();
          if (!canceled) setUser(fresh);
        } catch {
          // Nobody is signed in here: the link still did its job.
        }
      },
      () => {
        if (!canceled) setState('failed');
      },
    );
    return () => {
      canceled = true;
    };
    // Keyed on the token: the link is spent once, not again whenever the signed-in user changes.
  }, [token, setUser]);

  const resend = async () => {
    setResent('busy');
    try {
      await auth.resendVerification();
      setResent('sent');
    } catch {
      setResent('failed');
    }
  };

  return (
    <div className="auth page-enter">
      <div className="form-side">
        <article className="form-inner">
          <span className="eyebrow">Your account</span>
          <h1>Email verification</h1>
          <div aria-live="polite">
            {state === 'working' && <p className="muted">Checking your link…</p>}
            {state === 'ok' && (
              <div className="notice ok" role="status">
                <strong style={{ display: 'block' }}>Your email address is verified</strong>
                Thank you. We can now reach you about your account.
              </div>
            )}
            {state === 'failed' && (
              <div className="notice bad" role="alert">
                <strong style={{ display: 'block' }}>We could not verify that link</strong>
                {NEUTRAL_BAD_LINK}
              </div>
            )}
          </div>
          {state === 'failed' && user && (
            <div aria-live="polite" style={{ marginTop: 'var(--sp-3)' }}>
              {resent === 'sent' ? (
                <div className="notice ok" role="status">A new link is on its way to {user.email}.</div>
              ) : (
                <>
                  {resent === 'failed' && <div className="notice bad" role="alert">That did not work. Try again in a moment.</div>}
                  <div className="actions">
                    <button type="button" onClick={() => void resend()} disabled={resent === 'busy'}>{resent === 'busy' ? 'Sending…' : 'Send a new link'}</button>
                  </div>
                </>
              )}
            </div>
          )}
          <div className="actions">
            <a className="button quiet" href={href({ page: user ? 'dashboard' : 'login' })}>{user ? 'Go to your dashboard' : 'Log in'}</a>
          </div>
        </article>
      </div>
      <StorySide
        heading="A verified address keeps your account yours."
        points={[
          { icon: 'mail', title: 'Password recovery', body: 'A reset link can only reach an address we know is yours.' },
          { icon: 'bell', title: 'Assignments and reminders', body: 'Manager assignments and program nudges reach you outside the app.' },
          { icon: 'shield', title: 'One less way in', body: 'A verified address makes an unnoticed account takeover much harder.' },
        ]}
        note="Verification is not required to practice. Everything in WorkSim AI works while your address is unverified."
      />
    </div>
  );
}

/** The verified/unverified line on the profile page, with the same resend action as the shell notice. */
export function EmailVerificationStatus({ verified, email }: { verified: boolean; email: string }) {
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'failed'>('idle');
  const resend = async () => {
    setState('busy');
    try {
      await auth.resendVerification();
      setState('sent');
    } catch {
      setState('failed');
    }
  };
  if (verified) {
    return (
      <p className="small" style={{ margin: 0 }}>
        <span className="badge ok"><Icon name="check" size={13} /> Email verified</span>
      </p>
    );
  }
  return (
    <div className="row" style={{ gap: 'var(--sp-3)', flexWrap: 'wrap', alignItems: 'center' }}>
      <span className="badge warn"><Icon name="warning" size={13} /> Email not verified</span>
      <span aria-live="polite" className="small muted">
        {state === 'sent' ? `A new link is on its way to ${email}.` : state === 'failed' ? 'We could not send a new link. Try again in a moment.' : 'Verification keeps password recovery available.'}
      </span>
      {state !== 'sent' && (
        <button type="button" className="secondary small" onClick={() => void resend()} disabled={state === 'busy'}>{state === 'busy' ? 'Sending…' : 'Resend link'}</button>
      )}
    </div>
  );
}

const DISMISS_KEY = 'worksim.verify-notice-dismissed';

/** Per-browser-session dismissal. Storage can throw (private mode, blocked cookies), so every read and write is guarded. */
function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/** A quiet, dismissible reminder shown to a signed-in person whose address is not verified yet. */
export function VerifyEmailNotice() {
  const { user } = useStore();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'failed'>('idle');

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // A browser that refuses storage simply shows the notice again next time.
    }
  }, []);

  if (!user || user.emailVerified || dismissed) return null;

  const resend = async () => {
    setState('busy');
    try {
      await auth.resendVerification();
      setState('sent');
    } catch {
      setState('failed');
    }
  };

  return (
    <div className="notice warn verify-notice" role="status" aria-live="polite">
      <span className="icon-chip warn" aria-hidden="true"><Icon name="mail" size={16} /></span>
      <span className="verify-notice-text">
        <strong>Verify your email address</strong>
        <span className="small muted">
          {state === 'sent' ? `A new link is on its way to ${user.email}.` : state === 'failed' ? 'We could not send a new link. Try again in a moment.' : `We sent a link to ${user.email}. Opening it keeps password recovery available.`}
        </span>
      </span>
      <span className="verify-notice-actions">
        {state !== 'sent' && (
          <button type="button" className="secondary small" onClick={() => void resend()} disabled={state === 'busy'}>{state === 'busy' ? 'Sending…' : 'Resend link'}</button>
        )}
        <button type="button" className="ghost icon-button" aria-label="Dismiss the verification reminder" onClick={dismiss}><Icon name="close" size={16} /></button>
      </span>
    </div>
  );
}
